import { formatBrand } from './formatNames';

// What a product is made of: one substance and the colour or finish it comes in
// ("Tissu" + "beige", "Hévéa" + "foncé"). Either half may be empty — a listing that
// names a wood without a finish, or a colour without a substance, has still said
// something a submodel is told apart by.
//
// A PAIR rather than the fixed "wood" + "seat" fields this replaced: those two describe
// a chair and nothing else, so a lamp or a shelf had no field to answer in and a chair
// with three materials had nowhere to put the third.
export type MaterialPair = { material: string; color: string };

// A MATERIAL AND ITS FINISH AS ONE LINE, in sentence case: "Bois d'hévéa massif, foncé".
//
// One definition because three places print it — the info table's cell, a part's material
// dropdown, and the mesh's material menu — and they have to agree exactly: the mesh menu ticks
// the entry whose text matches the part's, and a second copy of this rule drifting by a capital
// letter would stop it matching.
//
// Sentence case because these are descriptions and not names: the listings SHOUT some of them
// and title-case others. Either half may be missing and what is left still says something.
export function materialText(pair: MaterialPair): string {
  const material = pair.material.trim();
  const head = material ? material.charAt(0).toUpperCase() + material.slice(1).toLowerCase() : '';
  return [head, pair.color.trim().toLowerCase()].filter(Boolean).join(', ');
}

// A "submodel" is one colour/material variant of the same product — the same manual can
// cover several web listings that differ only in what they are made of. This is how one
// is labelled and grouped: each pair read as "material colour", the pairs joined in the
// order the listing named them. '' when the listing names none — those photos group
// under the notice with no submodel row.
//
// The label is the grouping KEY for a notice's product photos, so two listings that
// differ in one material get their own row even when the others match: it carries every
// pair the submodels are told apart by, not the colour alone.
export type VariantAttrs = { materials?: MaterialPair[] };

export function variantLabel(v: VariantAttrs): string {
  return (v.materials || [])
    .map((p) => [p.material, p.color].map((s) => (s || '').trim()).filter(Boolean).join(' '))
    .filter(Boolean)
    .map(formatBrand)
    .join(' · ');
}

// The stored label, prettied for DISPLAY ONLY: plain sentence case (first letter up, the
// rest down) with the middots between materials swapped for commas — matching the info
// table's material cells, which are descriptions, not brand names. NEVER use this as the
// value that groups photos: that stays the raw variantLabel (a stable key). Every place
// that SHOWS a variant (the dropdown, the Product photos captions) runs it through here.
export function displayVariantLabel(label: string): string {
  const v = label.replace(/ · /g, ', ').trim();
  return v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : '';
}

// THE SUBMODEL, AS EVERY PLACE ON SCREEN CALLS IT: its number, then what it is made of.
//
// The number is its position in the link list, from 1. Four listings of the same chair have
// labels that differ by one word in the middle of a long line — "tissu beige, bois d'hévéa
// massif foncé" against "...effet blanchi" — and telling them apart in a dropdown meant
// reading to the end of each. A number is read at a glance, it is the order the links are
// in, and it is the same number in the table, the dropdown, the gallery headings and the
// link list.
//
// POSITIONAL, and so never stored. A label saved with a photograph keeps its words only
// (see ListingGallery): the group would otherwise be captioned "3 -" for as long as it
// exists, including after the third link is removed.
//
// `fallback` is what to call one whose listing has not been read yet — it has no materials
// to be named by, and it is still the second link in the list.
export function submodelLabel(v: VariantAttrs, at: number, fallback: string): string {
  return `${at + 1} - ${submodelName(v, fallback)}`;
}

// The same, without its number: for a place that prints the number apart (the sidebar's
// picker puts it beside the word "Submodel", above the name).
export function submodelName(v: VariantAttrs, fallback: string): string {
  return displayVariantLabel(variantLabel(v)) || fallback;
}
